
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import DatePicker from '@/components/shared/DatePicker.vue';
import { OrdersTableFilters } from '@/store/types';
import SelectComponent from '@/components/shared/Select.vue';

@Component<K12Filters>({
	components: {
		SelectComponent,
		DatePicker
	}
})
export default class K12Filters extends Vue {
	@Prop({ type: String, required: true }) private ordersFetchingDate!: string;
	@Prop({ type: Object, required: true }) private tableFilters!: OrdersTableFilters;
	@Prop({ type: String, default: 'kanban' }) private viewType!: string;
	@Getter('getRestaurantDeliveryInfo', { namespace: 'auth' }) private deliveryInfo!: Delivery | null;

	/**
	 * Get grade options from delivery info
	 *
	 * @return {string[]}
	 */
	private get gradeOptions(): string[] {
		return this.getSchoolOptions('grades');
	}

	/**
	 * Get class options from delivery info
	 *
	 * @return {string[]}
	 */
	private get classOptions(): string[] {
		return this.getSchoolOptions('classrooms');
	}

	/**
	 * Get teacher options from delivery info
	 *
	 * @return {string[]}
	 */
	private get teacherOptions(): string[] {
		return this.getSchoolOptions('teachers');
	}

	/**
	 * Get status options for filter dropdown
	 *
	 * @return {{ text: string; value: string }[]}
	 */
	private get statusOptions(): { text: string; value: string }[] {
		return [
			{ text: this.$t('orders.management.parent_card.order_card.pending'), value: 'pending' },
			{ text: this.$t('orders.management.parent_card.order_card.in_progress'), value: 'in-progress' },
			{ text: this.$t('orders.management.parent_card.order_card.completed'), value: 'completed' },
			{ text: this.$t('orders.management.parent_card.order_card.refunded'), value: 'refunded' },
			{ text: this.$t('orders.management.parent_card.order_card.cancelled_k12'), value: 'cancelled' }
		];
	}

	/**
	 * Check if clear filters button should be shown
	 *
	 * @return {boolean}
	 */
	private get showClearFiltersButton(): boolean {
		return this.tableFilters.selectedGrades.length > 0 ||
			this.tableFilters.selectedClasses.length > 0 ||
			this.tableFilters.selectedTeachers.length > 0 ||
			this.tableFilters.selectedStatuses.length > 0;
	}

	/**
	 * Handle date change event
	 *
	 * @param {string} date
	 * @return {void}
	 */
	private handleDateChange(date: string): void {
		this.$emit('date-changed', date);
	}

	/**
	 * Update a specific filter
	 *
	 * @param {string} filterName
	 * @param {string[]} value
	 * @return {void}
	 */
	private updateFilter(filterName: string, value: string[]): void {
		this.$emit('filter-changed', { filterName, value });
	}

	/**
	 * Get the school options for grades, classrooms and teachers.
	 * Loop through the schools to retrieve the values of each school for the field (e.g. "Hani, Liam", "Cédric, Ahmed" for teachers)
	 * and merge them together for the dropdown options (e.g. "Ahmed, Cédric, Hani, Liam")
	 *
	 * @param {string} field
	 * @return {string[]}
	 */
	private getSchoolOptions(field: 'grades' | 'classrooms' | 'teachers'): string[] {
		if (this.deliveryInfo?.type !== 'school' || !this.deliveryInfo.schools) {
			return [];
		}

		const options = new Set<string>();
		this.deliveryInfo.schools.forEach(school => {
			const fieldValue = school[field];
			if (fieldValue) {
				// Special condition for teachers field
				if (field === 'teachers' && school.isTeachersOpenEnded) {
					return;
				}
				this.addCommaSeparatedValues(options, fieldValue);
			}
		});

		return Array.from(options).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
	}

	/**
	 * Helper method to add comma-separated values to a set
	 *
	 * @param {Set<string>} set
	 * @param {string} values
	 * @return {void}
	 */
	private addCommaSeparatedValues(set: Set<string>, values: string): void {
		values.split(',').forEach(value => {
			const trimmed = value.trim();
			if (trimmed) {
				set.add(trimmed);
			}
		});
	}
}
